'use client';

import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import type { RefObject } from 'react';
import { createCustomerTaskFormConfig } from '../declarative-form/createCustomerTaskFormConfig';
import { DeclarativeForm } from '../declarative-form/DeclarativeForm';
import { Body } from '../typography';
import { Button } from './Button';
import { FlexContainer } from './FlexContainer';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './Select';
import type { UploadProps } from '../declarative-form';
import type { CustomerTaskDefinition, CustomerTaskInputDefinition, CustomerTaskLinkDefinition, CustomerTaskSelectDefinition } from '../types/customerTask';
interface CustomerTaskContentProps {
  onCopyLink?: (url: string) => void;
  onSubmit?: (data: Record<string, unknown>, task: CustomerTaskDefinition) => Promise<void> | void;
  task: CustomerTaskDefinition;
  formId?: string;
  serverErrors?: Record<string, string> | null;
  showSeparator?: boolean;
  uploadProps?: UploadProps;
  getValuesRef?: RefObject<(() => Record<string, unknown>) | null>;
}

/** Shared form, select, and link content for customer tasks. Apps own side effects through callbacks. */
export function CustomerTaskContent(t0) {
  const $ = _c(38);
  const {
    formId,
    getValuesRef,
    onCopyLink,
    onSubmit,
    serverErrors,
    showSeparator,
    task,
    uploadProps
  } = t0;
  let t1;
  if ($[0] !== task) {
    t1 = createCustomerTaskFormConfig(task);
    $[0] = task;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const formConfig = t1;
  if (task.taskType === "select") {
    let t2;
    if ($[2] !== formId || $[3] !== onCopyLink || $[4] !== onSubmit || $[5] !== serverErrors || $[6] !== showSeparator || $[7] !== task || $[8] !== uploadProps) {
      t2 = <CustomerTaskSelectContent {...{
        formId,
        onCopyLink,
        onSubmit,
        serverErrors,
        showSeparator,
        task,
        uploadProps
      }} />;
      $[2] = formId;
      $[3] = onCopyLink;
      $[4] = onSubmit;
      $[5] = serverErrors;
      $[6] = showSeparator;
      $[7] = task;
      $[8] = uploadProps;
      $[9] = t2;
    } else {
      t2 = $[9];
    }
    return t2;
  }
  if (task.taskType === "complete_link") {
    let t2;
    if ($[10] !== onSubmit || $[11] !== task) {
      t2 = event => {
        event.preventDefault();
        onSubmit?.({}, task);
      };
      $[10] = onSubmit;
      $[11] = task;
      $[12] = t2;
    } else {
      t2 = $[12];
    }
    let t3;
    if ($[13] !== task.link) {
      t3 = task.link?.label && <Body size="sm">{task.link.label}</Body>;
      $[13] = task.link;
      $[14] = t3;
    } else {
      t3 = $[14];
    }
    let t4;
    if ($[15] !== onCopyLink || $[16] !== task.link) {
      t4 = task.link?.url && onCopyLink && <Button className="w-fit" onClick={() => onCopyLink(task.link.url)} type="button" variant="secondary">Copy link</Button>;
      $[15] = onCopyLink;
      $[16] = task.link;
      $[17] = t4;
    } else {
      t4 = $[17];
    }
    let t5;
    if ($[18] !== formId || $[19] !== t2 || $[20] !== t3 || $[21] !== t4) {
      t5 = <form className="flex flex-col gap-4" id={formId} onSubmit={t2}>{t3}{t4}</form>;
      $[18] = formId;
      $[19] = t2;
      $[20] = t3;
      $[21] = t4;
      $[22] = t5;
    } else {
      t5 = $[22];
    }
    return t5;
  }
  if (!formConfig) {
    let t2;
    if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <Body className="text-content-secondary" size="sm">This task has no fields to complete.</Body>;
      $[23] = t2;
    } else {
      t2 = $[23];
    }
    return t2;
  }
  let t2;
  if ($[24] !== formId) {
    t2 = formId ? {
      id: formId
    } : undefined;
    $[24] = formId;
    $[25] = t2;
  } else {
    t2 = $[25];
  }
  let t3;
  if ($[26] !== onSubmit || $[27] !== task) {
    t3 = data => onSubmit?.(data, task);
    $[26] = onSubmit;
    $[27] = task;
    $[28] = t3;
  } else {
    t3 = $[28];
  }
  let t4;
  if ($[29] !== formConfig || $[30] !== formId || $[31] !== getValuesRef || $[32] !== serverErrors || $[33] !== showSeparator || $[34] !== t2 || $[35] !== t3 || $[36] !== uploadProps) {
    t4 = <DeclarativeForm actionsComponent={_temp} config={formConfig} formId={formId} formProps={t2} getValuesRef={getValuesRef} onSubmit={t3} serverErrors={serverErrors} showSeparator={showSeparator} uploadProps={uploadProps} />;
    $[29] = formConfig;
    $[30] = formId;
    $[31] = getValuesRef;
    $[32] = serverErrors;
    $[33] = showSeparator;
    $[34] = t2;
    $[35] = t3;
    $[36] = uploadProps;
    $[37] = t4;
  } else {
    t4 = $[37];
  }
  return t4;
}
function _temp() {
  return null;
}
function CustomerTaskSelectContent(props) {
  const $ = _c(21);
  const options = props.task.options?.filter(_temp2) ?? [];
  const [selectedLabel, setSelectedLabel] = useState(options[0]?.label ?? "");
  const selectedTask = options.find(option_0 => option_0.label === selectedLabel)?.value;
  const T0 = FlexContainer;
  const t0 = "gap-4";
  const t1 = "column";
  let t2;
  if ($[0] !== props.task.label) {
    t2 = props.task.label && <Body weight="medium">{props.task.label}</Body>;
    $[0] = props.task.label;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const T1 = Select;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <SelectTrigger className="w-full"><SelectValue placeholder="Select an option" /></SelectTrigger>;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const T2 = SelectContent;
  const t4 = options.map(_temp3);
  let t5;
  if ($[3] !== T2 || $[4] !== t4) {
    t5 = <T2>{t4}</T2>;
    $[3] = T2;
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== T1 || $[7] !== selectedLabel || $[8] !== setSelectedLabel || $[9] !== t3 || $[10] !== t5) {
    t6 = <T1 onValueChange={setSelectedLabel} value={selectedLabel}>{t3}{t5}</T1>;
    $[6] = T1;
    $[7] = selectedLabel;
    $[8] = setSelectedLabel;
    $[9] = t3;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== props || $[13] !== selectedLabel || $[14] !== selectedTask) {
    t7 = selectedTask && <CustomerTaskContent {...props} key={selectedLabel} task={selectedTask} />;
    $[12] = props;
    $[13] = selectedLabel;
    $[14] = selectedTask;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== T0 || $[17] !== t2 || $[18] !== t6 || $[19] !== t7) {
    t8 = <T0 className={t0} direction={t1}>{t2}{t6}{t7}</T0>;
    $[16] = T0;
    $[17] = t2;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  return t8;
}
function _temp3(option_1) {
  return <SelectItem key={option_1.label} value={option_1.label}>{option_1.label}</SelectItem>;
}
function _temp2(option) {
  return typeof option.label === "string" && option.value != null;
}